<script setup lang="ts">
// @vueuse/core n'est PAS auto-importé dans donoor.org (pas de @vueuse/nuxt
// dans les modules — seulement transpilé). Import explicite obligatoire, sinon
// « useIntersectionObserver is not defined » en SSR. useIntersectionObserver
// est SSR-safe : il n'attache l'observer que côté client (quand `sentinel` est
// monté), no-op au rendu serveur.
import { useIntersectionObserver } from "@vueuse/core";

const { t } = useI18n({ useScope: "local" });

/**
 * InfiniteScrollSentinel — déclenche le chargement de la page suivante quand
 * l'élément sentinelle entre dans le viewport, avec un bouton « Voir plus » de
 * secours pour l'accessibilité clavier et les crawlers SEO (qui n'exécutent
 * pas l'IntersectionObserver).
 *
 * Le parent garde la responsabilité du fetch : ce composant émet `load`
 * (garde anti-double-fetch via `canLoad`).
 */
const props = defineProps<{
  /** Reste-t-il des pages à charger ? (hasNextPage du parent) */
  hasMore: boolean;
  /** Un fetch de page suivante est-il déjà en cours ? (évite les doublons) */
  loading: boolean;
  /**
   * La dernière tentative de page suivante a-t-elle échoué ? Si oui, on N'AUTO-
   * retente PAS (l'observer relancerait en boucle sur une coupure réseau) : on
   * affiche un bouton « Réessayer » MANUEL. Karim 2026-07-23.
   */
  error?: boolean;
  /** Libellé du bouton fallback. */
  label?: string;
}>();

const emit = defineEmits<{ load: [] }>();

const sentinel = useTemplateRef<HTMLElement>("sentinel");

// Garde : on n'émet que si on peut réellement charger (pas déjà en cours, il
// reste des pages, ET pas en état d'erreur — sinon l'observer retenterait en
// boucle une requête qui échoue). Après un clic « Réessayer », le parent
// efface `error` → l'auto-chargement reprend.
const canLoad = computed(() => props.hasMore && !props.loading && !props.error);

function requestLoad() {
  if (canLoad.value) emit("load");
}

// Clic « Réessayer » : action MANUELLE explicite → on relance même si `error`
// est levé (c'est justement ce qu'on veut retenter). On respecte quand même
// `hasMore`/`loading` pour ne pas doubler un fetch en cours.
function retry() {
  if (props.hasMore && !props.loading) emit("load");
}

// useIntersectionObserver (@vueuse/core, auto-importé) est SSR-safe : côté
// serveur il no-op, côté client il s'attache quand `sentinel` est monté.
// ⚠️ NE PAS faire `await import(...)` ici : un await top-level rend le
// composant async et, sans <Suspense>, l'observer ne se monterait jamais
// (bug constaté : le scroll ne déclenchait aucun fetch, seul le bouton).
const isIntersecting = ref(false);
useIntersectionObserver(
  sentinel,
  (entries) => {
    isIntersecting.value = entries[0]?.isIntersecting ?? false;
    if (isIntersecting.value) requestLoad();
  },
  // rootMargin : précharge ~400px AVANT que la sentinelle soit visible.
  { rootMargin: "400px 0px" },
);

// Un déclenchement observer/clic pendant `loading` est jeté par la garde
// canLoad et l'observer ne re-tire pas tant que la sentinelle ne RE-rentre
// pas dans le viewport → scroll infini mort (bug constaté : spinner puis
// bouton « Voir plus » qui revient sans rien charger). Quand le fetch se
// termine (canLoad repasse true) et que la sentinelle est toujours visible,
// on relance. Enchaîne aussi automatiquement les pages dont tous les items
// sont masqués par les filtres client (catégorie/ville) jusqu'à en trouver.
watch(canLoad, (ok) => {
  if (ok && isIntersecting.value) requestLoad();
});
</script>

<template>
  <div v-if="hasMore" class="w-full flex flex-col items-center gap-4 py-8">
    <!-- Sentinelle observée (invisible, sans hauteur propre parasite).
         Désactivée en état d'erreur : pas d'auto-retry en boucle. -->
    <div v-if="!error" ref="sentinel" aria-hidden="true" class="h-px w-full" />

    <!-- Spinner pendant le chargement de la page suivante -->
    <div
      v-if="loading"
      class="flex items-center gap-2 text-sm text-gray-500"
      role="status"
      aria-live="polite"
    >
      <UIcon name="i-lucide-loader-circle" class="w-5 h-5 animate-spin" />
      <span>{{ t('status.chargement') }}</span>
    </div>

    <!-- Erreur de page suivante : la liste déjà chargée reste visible ; on
         propose un retry MANUEL (pas d'auto-relance en boucle). -->
    <div v-else-if="error" class="flex flex-col items-center gap-2 text-center">
      <p class="text-sm text-gray-500">
        {{ t('erreur.message') }}
      </p>
      <button
        type="button"
        class="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-gray-200 bg-white text-sm font-semibold text-(--color-ink) hover:border-[#0058fb]/40 hover:shadow-sm transition-all"
        @click="retry"
      >
        <UIcon name="i-lucide-rotate-ccw" class="w-4 h-4" />
        {{ t('erreur.reessayer') }}
      </button>
    </div>

    <!-- Fallback SEO / clavier : un vrai lien-bouton cliquable. Les crawlers
         qui n'exécutent pas l'IntersectionObserver peuvent suivre ce bouton
         (et un utilisateur au clavier aussi). -->
    <button
      v-else
      type="button"
      class="px-6 py-2.5 rounded-full border border-gray-200 bg-white text-sm font-semibold text-(--color-ink) hover:border-[#0058fb]/40 hover:shadow-sm transition-all"
      @click="requestLoad"
    >
      {{ label ?? t('fallback.voirPlus') }}
    </button>
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/components/shared/InfiniteScrollSentinel.json"></i18n>
